import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  createMockAdapter,
  createMemberfulAdapter,
  createDemoAdapter,
  type BillingProviderAdapter,
} from "@constructorfleet/ui";

import { BillingContext, type BillingContextValue } from "./billing-context";
import { BILLING_PROVIDER_CHANGED_EVENT, readBillingProviderMeta } from "./billing-provider-meta";
import { IS_TEST_MODE } from "../constants";
import { getSessionToken } from "../lib/api-client";

async function loadBillingProviderFromApi(): Promise<string | null> {
  try {
    const response = await fetch("/api/website/billing-provider");
    if (!response.ok) return null;
    const payload = (await response.json()) as { ok?: boolean; billingProvider?: string };
    return payload.billingProvider ?? null;
  } catch {
    return null;
  }
}

function buildAdapter(provider?: string): BillingProviderAdapter {
  // In development / demo mode use the mock adapter so the billing portal
  // is fully functional without any external provider configured.
  if (IS_TEST_MODE || import.meta.env.MODE === "development") {
    return createMockAdapter();
  }

  // Production path — read the billing provider from a meta tag injected by
  // the server, or fall back to mock.
  const resolvedProvider = provider ?? readBillingProviderMeta();

  const apiAdapterConfig = { apiBaseUrl: "", getAuthToken: getSessionToken };

  switch (resolvedProvider) {
    case "demo":
    case "paddle":
    case "stripe":
      // Paddle and Stripe use the same REST API endpoints as the demo adapter.
      // The provider-specific logic (checkout URLs, webhook verification) is
      // handled server-side; the client adapter just calls our API.
      return createDemoAdapter(apiAdapterConfig);
    case "memberful":
      return createMemberfulAdapter(apiAdapterConfig);
    default:
      return createMockAdapter();
  }
}

export function BillingProvider({ children }: { children: ReactNode }) {
  const [billingProvider, setBillingProvider] = useState(() => readBillingProviderMeta());

  useEffect(() => {
    const handleProviderChange = (event: Event) => {
      const nextProvider = (event as CustomEvent<string>).detail ?? readBillingProviderMeta();
      setBillingProvider(nextProvider);
    };

    window.addEventListener(BILLING_PROVIDER_CHANGED_EVENT, handleProviderChange);
    return () => {
      window.removeEventListener(BILLING_PROVIDER_CHANGED_EVENT, handleProviderChange);
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const nextProvider = await loadBillingProviderFromApi();
      if (!active || !nextProvider) return;
      setBillingProvider(nextProvider);
    })();

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<BillingContextValue>(
    () => ({ adapter: buildAdapter(billingProvider), billingProvider }),
    [billingProvider]
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}
